<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ConditionGroup from './ConditionGroup.vue';
import ConditionRow from './ConditionRow.vue';
import QueryOperatorSelect from 'dashboard/components/widgets/FilterInput/QueryOperatorSelect.vue';
import {
  buildConditionFormula,
  getAttributes,
  groupConditionAttributes,
  isConditionGroup,
} from 'dashboard/helper/automationHelper';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  automationTypes: {
    type: Object,
    required: true,
  },
  automation: {
    type: Object,
    required: true,
  },
  allCustomAttributes: {
    type: Array,
    default: () => [],
  },
  mode: {
    type: String,
    default: 'create',
  },
  errors: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const conditionTypes = computed(() =>
  getAttributes(props.automationTypes, props.automation.event_name)
);

const attributeGroups = computed(() =>
  groupConditionAttributes(
    conditionTypes.value.map(attribute => ({
      ...attribute,
      // Custom attributes are named by the user, so only standard ones are translated.
      name: attribute.customAttributeType
        ? attribute.name
        : t(`AUTOMATION.ATTRIBUTES.${attribute.name}`),
    }))
  ).map(group => ({ ...group, name: t(`AUTOMATION.CONDITION.${group.name}`) }))
);

// Rows and the formula underneath refer to the same numbers, so both are
// derived from one depth-first walk of the tree.
const rows = computed(() => {
  let number = 0;
  return props.modelValue.map((node, nodeIndex) => {
    // The operator joins an entry to the next one, so the last entry of a list
    // never shows one. Position decides this, not the stored value, which may
    // not have been normalised yet.
    const isLast = nodeIndex === props.modelValue.length - 1;

    if (!isConditionGroup(node)) {
      number += 1;
      return { type: 'condition', node, nodeIndex, number, isLast };
    }

    return {
      type: 'group',
      node,
      nodeIndex,
      isLast,
      children: node.conditions.map((condition, childIndex) => {
        number += 1;
        return {
          node: condition,
          nodeIndex,
          childIndex,
          number,
          isLast: childIndex === node.conditions.length - 1,
        };
      }),
    };
  });
});

const formula = computed(() =>
  buildConditionFormula(props.modelValue, {
    and: t('AUTOMATION.CONDITION.OPERATOR_AND'),
    or: t('AUTOMATION.CONDITION.OPERATOR_OR'),
  })
);

const hasSeveralConditions = computed(
  () => rows.value.reduce((n, row) => n + (row.children?.length ?? 1), 0) > 1
);

const newCondition = () => {
  const [first] = conditionTypes.value;
  return {
    attribute_key: first.key,
    filter_operator: first.filterOperators[0].value,
    values: '',
    query_operator: 'and',
    custom_attribute_type: first.customAttributeType ?? '',
  };
};

const update = nodes => emit('update:modelValue', nodes);

// The last entry of a list has nothing after it to join to, so it carries no
// operator. Everything before it must carry one.
const normalise = nodes =>
  nodes.map((node, index) => {
    const isLast = index === nodes.length - 1;
    const withOperator = {
      ...node,
      query_operator: isLast ? null : (node.query_operator ?? 'and'),
    };
    return isConditionGroup(node)
      ? { ...withOperator, conditions: normalise(node.conditions) }
      : withOperator;
  });

const replaceAt = (nodes, index, value) =>
  nodes.map((node, i) => (i === index ? value : node));

const setCondition = ({ nodeIndex, childIndex }, value) => {
  const nodes = props.modelValue;
  if (childIndex === undefined) {
    update(normalise(replaceAt(nodes, nodeIndex, value)));
    return;
  }

  const group = nodes[nodeIndex];
  update(
    normalise(
      replaceAt(nodes, nodeIndex, {
        ...group,
        conditions: replaceAt(group.conditions, childIndex, value),
      })
    )
  );
};

const removeCondition = ({ nodeIndex, childIndex }) => {
  const nodes = props.modelValue;
  if (childIndex === undefined) {
    update(normalise(nodes.filter((_, i) => i !== nodeIndex)));
    return;
  }

  const group = nodes[nodeIndex];
  const conditions = group.conditions.filter((_, i) => i !== childIndex);
  // A group with nothing left in it is noise, so it goes with its last condition.
  update(
    normalise(
      conditions.length
        ? replaceAt(nodes, nodeIndex, { ...group, conditions })
        : nodes.filter((_, i) => i !== nodeIndex)
    )
  );
};

// Changing the attribute invalidates the operator and value picked for the
// previous one.
const resetCondition = position => {
  const attributeKey =
    position.childIndex === undefined
      ? props.modelValue[position.nodeIndex].attribute_key
      : props.modelValue[position.nodeIndex].conditions[position.childIndex]
          .attribute_key;

  const type = conditionTypes.value.find(item => item.key === attributeKey);
  const current =
    position.childIndex === undefined
      ? props.modelValue[position.nodeIndex]
      : props.modelValue[position.nodeIndex].conditions[position.childIndex];

  setCondition(position, {
    ...current,
    filter_operator: type.filterOperators[0].value,
    values: '',
  });
};

const addCondition = () =>
  update(normalise([...props.modelValue, newCondition()]));

const addConditionToGroup = nodeIndex => {
  const group = props.modelValue[nodeIndex];
  update(
    normalise(
      replaceAt(props.modelValue, nodeIndex, {
        ...group,
        conditions: [...group.conditions, newCondition()],
      })
    )
  );
};

const addGroup = () =>
  update(
    normalise([
      ...props.modelValue,
      { query_operator: null, conditions: [newCondition(), newCondition()] },
    ])
  );

const removeGroup = nodeIndex =>
  update(normalise(props.modelValue.filter((_, i) => i !== nodeIndex)));

// A group joins to whatever follows it, so it carries an operator of its own.
const setGroupOperator = (nodeIndex, queryOperator) =>
  update(
    normalise(
      replaceAt(props.modelValue, nodeIndex, {
        ...props.modelValue[nodeIndex],
        query_operator: queryOperator,
      })
    )
  );

const errorFor = number => props.errors[`condition_${number - 1}`] || '';
</script>

<template>
  <div>
    <template v-for="row in rows" :key="`${row.type}-${row.nodeIndex}`">
      <ConditionRow
        v-if="row.type === 'condition'"
        :condition="row.node"
        :number="row.number"
        :show-query-operator="!row.isLast"
        :attribute-groups="attributeGroups"
        :automation-types="automationTypes"
        :automation="automation"
        :all-custom-attributes="allCustomAttributes"
        :mode="mode"
        :error="errorFor(row.number)"
        @update:condition="setCondition(row, $event)"
        @reset="resetCondition(row)"
        @remove="removeCondition(row)"
      />

      <template v-else>
        <ConditionGroup
          :children="row.children"
          :attribute-groups="attributeGroups"
          :automation-types="automationTypes"
          :automation="automation"
          :all-custom-attributes="allCustomAttributes"
          :mode="mode"
          :errors="errors"
          @update-condition="setCondition"
          @reset-condition="resetCondition"
          @remove-condition="removeCondition"
          @add-condition="addConditionToGroup(row.nodeIndex)"
          @remove="removeGroup(row.nodeIndex)"
        />
        <QueryOperatorSelect
          v-if="!row.isLast"
          :model-value="row.node.query_operator"
          @update:model-value="setGroupOperator(row.nodeIndex, $event)"
        />
      </template>
    </template>

    <div class="flex gap-2 mt-4">
      <NextButton
        icon="i-lucide-plus"
        blue
        faded
        sm
        :label="$t('AUTOMATION.ADD.CONDITION_BUTTON_LABEL')"
        @click="addCondition"
      />
      <NextButton
        icon="i-lucide-parentheses"
        slate
        faded
        sm
        :label="$t('AUTOMATION.CONDITION.ADD_GROUP')"
        @click="addGroup"
      />
    </div>

    <p v-if="hasSeveralConditions" class="mt-3 mb-0 text-xs text-n-slate-10">
      {{ $t('AUTOMATION.CONDITION.FORMULA_HINT') }}
      <span class="font-medium text-n-slate-11">{{ formula }}</span>
    </p>
  </div>
</template>
